import React from "react";
import { withNamespaces } from "react-i18next";
import { initDb } from "../../tools/conf";
import { UserDatabaseConnector } from "../../dataconnector/usersDatabaseConnector";

const uConnect = new UserDatabaseConnector();

/**
 * Types de bannières disponibles :
 * - info: Annonce d'évolution/nouveauté (bleu/violet)
 * - warning: Avertissement paiement (orange)
 * - mfa: Rappel MFA requis (rouge/orange)
 */
const BANNER_STYLES = {
  info: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    icon: "fas fa-bullhorn",
    textColor: "white",
    linkColor: "rgba(255,255,255,0.9)",
  },
  warning: {
    background: "linear-gradient(135deg, #f6c23e 0%, #e74a3b 100%)",
    icon: "fas fa-exclamation-triangle",
    textColor: "white",
    linkColor: "rgba(255,255,255,0.9)",
  },
  mfa: {
    background: "linear-gradient(135deg, #e74a3b 0%, #be2617 100%)",
    icon: "fas fa-shield-alt",
    textColor: "white",
    linkColor: "rgba(255,255,255,0.9)",
  },
  success: {
    background: "linear-gradient(135deg, #1cc88a 0%, #13855c 100%)",
    icon: "fas fa-check-circle",
    textColor: "white",
    linkColor: "rgba(255,255,255,0.9)",
  },
};

class GlobalBanner extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      banners: [],
      loaded: false,
      dismissed: [],
    };
    this.bannerRef = React.createRef();
  }

  componentDidMount = async () => {
    await this.loadBanners();
  };

  componentDidUpdate(prevProps, prevState) {
    // Mettre à jour le margin-top de section-container quand les bannières changent
    if (prevState.banners.length !== this.state.banners.length) {
      this.updateLayoutMargin();
    }
  }

  componentWillUnmount() {
    // Réinitialiser le margin quand le composant est démonté
    this.resetLayoutMargin();
  }

  updateLayoutMargin = () => {
    // Attendre le prochain tick pour que le DOM soit à jour
    setTimeout(() => {
      const bannerHeight = this.bannerRef.current?.offsetHeight || 0;
      const sectionContainer = document.querySelector(".section-container");
      const asideInner = document.querySelector(".aside-inner");

      if (sectionContainer) {
        // La hauteur de base du navbar est 55px (définie dans les variables SCSS)
        const baseMargin = 55;
        sectionContainer.style.marginTop = `${baseMargin + bannerHeight}px`;
      }

      if (asideInner) {
        // Ajuster aussi le padding-top du sidebar
        const basePadding = 55;
        asideInner.style.paddingTop = `${basePadding + bannerHeight}px`;
      }
    }, 0);
  };

  resetLayoutMargin = () => {
    const sectionContainer = document.querySelector(".section-container");
    const asideInner = document.querySelector(".aside-inner");

    if (sectionContainer) {
      sectionContainer.style.marginTop = "";
    }
    if (asideInner) {
      asideInner.style.paddingTop = "";
    }
  };

  loadBanners = async () => {
    try {
      const userData = await uConnect._getUserData();
      console.log("[GlobalBanner] userData:", userData?.success, userData?.data?.zooId);

      if (!userData.success || !userData.data) {
        console.log("[GlobalBanner] Pas de userData, skip");
        this.setState({ loaded: true });
        return;
      }

      const zooId = userData.data.zooId || userData.data.userZoo?.value;
      if (!zooId) {
        console.log("[GlobalBanner] Pas de zooId, skip");
        this.setState({ loaded: true });
        return;
      }

      const db = initDb();
      const zooDoc = await db.collection("zoo").doc(zooId).get();

      if (!zooDoc.exists) {
        console.log("[GlobalBanner] Zoo doc n'existe pas:", zooId);
        this.setState({ loaded: true });
        return;
      }

      const zooData = zooDoc.data();
      console.log("[GlobalBanner] zooData.globalBanner:", zooData.globalBanner);
      console.log("[GlobalBanner] zooData.paymentWarning:", zooData.paymentWarning);
      console.log("[GlobalBanner] zooData.authConfig:", zooData.authConfig);
      const banners = [];

      // Récupérer les bannières dismissées du localStorage
      const dismissedKey = `dismissed_banners_${zooId}`;
      const dismissed = JSON.parse(localStorage.getItem(dismissedKey) || "[]");

      // 1. Bannière d'annonce personnalisée (info)
      if (zooData.globalBanner?.enabled && zooData.globalBanner?.message) {
        const bannerId = `info_${zooData.globalBanner.updatedAt || "default"}`;
        if (!dismissed.includes(bannerId)) {
          banners.push({
            id: bannerId,
            type: "info",
            message: zooData.globalBanner.message,
            link: zooData.globalBanner.link || null,
            linkText: zooData.globalBanner.linkText || null,
            dismissable: zooData.globalBanner.dismissable !== false,
          });
        }
      }

      // 2. Bannière de paiement en retard (warning)
      if (zooData.paymentWarning?.enabled) {
        const bannerId = "payment_warning";
        if (!dismissed.includes(bannerId)) {
          banners.push({
            id: bannerId,
            type: "warning",
            message: zooData.paymentWarning.message || this.props.t("banner.paymentWarningDefault"),
            link: zooData.paymentWarning.link || null,
            linkText: zooData.paymentWarning.linkText || null,
            dismissable: false, // Jamais dismissable
          });
        }
      }

      // 3. Bannière MFA selon le mode d'enforcement
      const mfaEnforcement = zooData.authConfig?.mfaEnforcement
        || (zooData.authConfig?.mfaRequired ? "all" : "optional"); // rétrocompat
      const mfaShowBanner =
        mfaEnforcement === "all" ||
        mfaEnforcement === "auto_enrollment" ||
        (mfaEnforcement === "selected" && userData.data?.mfaEnforced === true);

      if (mfaShowBanner) {
        const mfaStatus = await uConnect._checkUserMFAStatus();
        if (!mfaStatus.enrolled) {
          const bannerId = "mfa_required";
          if (!dismissed.includes(bannerId)) {
            // Calculer les jours restants si période de grâce
            let daysRemaining = null;
            if (zooData.authConfig?.mfaGracePeriodDays) {
              const createdAt = userData.data.createdAt || Date.now();
              const gracePeriodEnd = new Date(createdAt);
              gracePeriodEnd.setDate(
                gracePeriodEnd.getDate() + zooData.authConfig.mfaGracePeriodDays
              );
              daysRemaining = Math.max(
                0,
                Math.ceil((gracePeriodEnd - new Date()) / (1000 * 60 * 60 * 24))
              );
            }

            // Auto-enrollment = toujours dismissable (invitation, pas obligation)
            // selected/all = obligation → dismissable seulement pendant la grâce
            const isDismissable = mfaEnforcement === "auto_enrollment"
              || (daysRemaining !== null && daysRemaining > 0);

            banners.push({
              id: bannerId,
              type: daysRemaining !== null && daysRemaining > 0 ? "warning" : "mfa",
              message:
                mfaEnforcement === "auto_enrollment"
                  ? this.props.t("banner.mfaAutoEnrollment")
                  : daysRemaining !== null && daysRemaining > 0
                    ? this.props.t("banner.mfaGracePeriod", { days: daysRemaining })
                    : this.props.t("banner.mfaRequired"),
              link: "/userSettings?tab=security",
              linkText: this.props.t("banner.mfaSetupLink"),
              dismissable: isDismissable,
            });
          }
        }
      }

      console.log("[GlobalBanner] Bannières trouvées:", banners.length, banners);

      this.setState(
        {
          banners,
          loaded: true,
          dismissed,
          zooId,
        },
        () => {
          // Mettre à jour le layout après le chargement initial
          if (banners.length > 0) {
            this.updateLayoutMargin();
          }
        }
      );
    } catch (error) {
      console.error("[GlobalBanner] Erreur chargement bannières:", error);
      this.setState({ loaded: true });
    }
  };

  handleDismiss = (bannerId) => {
    const { zooId, dismissed } = this.state;
    const newDismissed = [...dismissed, bannerId];
    const newBanners = this.state.banners.filter((b) => b.id !== bannerId);

    // Sauvegarder dans localStorage
    const dismissedKey = `dismissed_banners_${zooId}`;
    localStorage.setItem(dismissedKey, JSON.stringify(newDismissed));

    // Mettre à jour le state
    this.setState(
      {
        dismissed: newDismissed,
        banners: newBanners,
      },
      () => {
        // Mettre à jour le layout après la fermeture
        if (newBanners.length === 0) {
          this.resetLayoutMargin();
        } else {
          this.updateLayoutMargin();
        }
      }
    );
  };

  renderBanner = (banner) => {
    const style = BANNER_STYLES[banner.type] || BANNER_STYLES.info;

    return (
      <div
        key={banner.id}
        style={{
          background: style.background,
          padding: "12px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "12px",
          position: "relative",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        }}
      >
        <i
          className={style.icon}
          style={{
            color: style.textColor,
            fontSize: "16px",
          }}
        ></i>

        <span
          style={{
            color: style.textColor,
            fontSize: "14px",
            fontWeight: 500,
          }}
        >
          {banner.message}
        </span>

        {banner.link && (
          <a
            href={banner.link}
            style={{
              color: style.linkColor,
              fontSize: "14px",
              fontWeight: 600,
              textDecoration: "underline",
              marginLeft: "8px",
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => (e.target.style.opacity = "0.8")}
            onMouseLeave={(e) => (e.target.style.opacity = "1")}
          >
            {banner.linkText || this.props.t("banner.learnMore")}
            <i className="fas fa-arrow-right" style={{ marginLeft: "6px", fontSize: "12px" }}></i>
          </a>
        )}

        {banner.dismissable && (
          <button
            onClick={() => this.handleDismiss(banner.id)}
            style={{
              position: "absolute",
              right: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "50%",
              width: "28px",
              height: "28px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) => (e.target.style.background = "rgba(255,255,255,0.3)")}
            onMouseLeave={(e) => (e.target.style.background = "rgba(255,255,255,0.2)")}
            title={this.props.t("banner.dismiss")}
          >
            <i
              className="fas fa-times"
              style={{ color: style.textColor, fontSize: "12px", pointerEvents: "none" }}
            ></i>
          </button>
        )}
      </div>
    );
  };

  render() {
    const { banners, loaded } = this.state;

    if (!loaded || banners.length === 0) {
      return null;
    }

    return (
      <div
        ref={this.bannerRef}
        style={{
          position: "relative",
          zIndex: 1100,
          width: "100%",
        }}
      >
        {banners.map((banner) => this.renderBanner(banner))}
      </div>
    );
  }
}

export default withNamespaces()(GlobalBanner);
